/**
 * The client's view of a game of hartenjagen.
 *
 * Deliberately a sibling of `store/live.ts` rather than a widening of it. The
 * two hold different states and react to different messages; the only thing
 * they genuinely share is the socket, which is why `GameSocket` is a class both
 * can drive rather than something either of them owns.
 */

import { create } from 'zustand';
import type { Card } from '@shared/types/game';
import type { HeartsGameState } from '@shared/types/hearts';
import { isHeartsState } from '@shared/types/hearts';
import type { ServerMessage } from '@shared/types/websocket';
import type { ConnectionStatus } from '@/live/socket';

export interface HeartsNotice {
  id: number;
  text: string;
}

export interface HeartsData {
  state: HeartsGameState | null;
  seat: number | null;
  code: string | null;
  status: ConnectionStatus;
  error: string | null;
  /** The code behind `error`, so a stale one can be retired. */
  errorCode: string | null;
  notices: HeartsNotice[];
  /** The three cards picked in the passing phase, before they are sent. */
  selection: Card[];
}

interface HeartsActions {
  handle: (message: ServerMessage) => void;
  setStatus: (status: ConnectionStatus) => void;
  toggleSelection: (card: Card) => void;
  clearSelection: () => void;
  reset: () => void;
}

const EMPTY: HeartsData = {
  state: null,
  seat: null,
  code: null,
  status: 'connecting',
  error: null,
  errorCode: null,
  notices: [],
  selection: [],
};

let noticeId = 0;

/**
 * Errors that stop being true on their own.
 *
 * Only this one so far: everything else — an illegal card, a bad bid — is about
 * what you did and stays until you acknowledge it.
 */
function staleAfterTurnChange(code: string | null, isMyTurn: boolean): boolean {
  return code === 'NOT_YOUR_TURN' && isMyTurn;
}

const sameCard = (a: Card, b: Card) => a.suit === b.suit && a.rank === b.rank;

function reduce(prev: HeartsData, message: ServerMessage): Partial<HeartsData> {
  const notice = (text: string): Partial<HeartsData> => ({
    notices: [...prev.notices, { id: ++noticeId, text }].slice(-4),
  });

  const nameOf = (seat: number) => prev.state?.players[seat]?.name ?? `Speler ${seat + 1}`;

  switch (message.type) {
    case 'joined':
      // Three games share this message now, so each store checks it is theirs.
      if (!isHeartsState(message.state)) return {};
      return {
        seat: message.seat,
        state: message.state,
        code: message.state.code,
        error: null,
        errorCode: null,
      };

    case 'state-update': {
      if (!isHeartsState(message.state)) return {};

      // Retire an error the new snapshot has made untrue.
      const cleared = staleAfterTurnChange(prev.errorCode, message.state.turnSeat === prev.seat)
        ? { error: null, errorCode: null }
        : {};

      // A fresh hand clears anything still selected from the last one.
      return {
        state: message.state,
        code: message.state.code,
        selection: message.state.phase === 'passing' ? prev.selection : [],
        ...cleared,
      };
    }

    case 'error':
      /*
       * Remember the code, not just the sentence.
       *
       * "Je bent niet aan de beurt" stays on screen until something replaces
       * it, so an impatient tap during someone else's turn leaves that banner
       * sitting there — still visible when your turn arrives and the panel
       * below it says the opposite. Keeping the code lets the next snapshot
       * retire it the moment it stops being true.
       */
      return { error: message.message, errorCode: message.code };

    case 'player-joined':
      return notice(`${message.name} doet mee`);

    case 'player-left':
      return notice(`${nameOf(message.seat)} is de verbinding kwijt`);

    case 'player-reconnected':
      return notice(`${nameOf(message.seat)} is er weer`);

    case 'hand-dealt':
      return notice(
        message.direction === 'none'
          ? `Ronde ${message.handNo}: niets doorgeven`
          : `Ronde ${message.handNo}: drie kaarten ${
              { left: 'naar links', right: 'naar rechts', across: 'naar de overkant' }[
                message.direction
              ]
            }`
      );

    case 'cards-exchanged':
      return { ...notice('De kaarten zijn geruild'), selection: [] };

    case 'trick-won':
      return notice(`${nameOf(message.seat)} pakt de slag`);

    case 'hand-scored':
      if (message.moonSeat !== null) {
        // Worth shouting about: it is the one move the whole game is played
        // around, and it turns the round inside out.
        return notice(`${nameOf(message.moonSeat)} schiet de maan! Iedereen 26 erbij.`);
      }
      return notice('Ronde geteld');

    case 'game-ended': {
      const winner = message.standings[0];
      return notice(`${winner.name} wint met ${winner.total} punten`);
    }

    // Already visible in the snapshot that follows, or not ours.
    case 'card-played':
    case 'round-dealt':
    case 'bid-placed':
    case 'all-bids-in':
    case 'round-scored':
    case 'deal-dealt':
    case 'trump-chosen':
    case 'trump-passed':
    case 'deal-scored':
    case 'roem-claimed':
      return {};

    default: {
      const exhaustive: never = message;
      console.warn('unhandled server message', exhaustive);
      return {};
    }
  }
}

export const useHearts = create<HeartsData & HeartsActions>()((set, get) => ({
  ...EMPTY,

  handle: (message) => set((prev) => reduce(prev, message)),
  setStatus: (status) => set({ status }),

  toggleSelection: (card) => {
    const { selection } = get();
    const already = selection.some((chosen) => sameCard(chosen, card));
    if (already) {
      set({ selection: selection.filter((chosen) => !sameCard(chosen, card)) });
      return;
    }
    // Three, and the third replaces nothing — tapping a fourth is a no-op
    // rather than a silent swap, which would be very easy to do by accident.
    if (selection.length >= 3) return;
    set({ selection: [...selection, card] });
  },

  clearSelection: () => set({ selection: [] }),
  reset: () => set({ ...EMPTY }),
}));

export const myHeartsPlayer = (data: HeartsData) =>
  data.seat === null ? null : (data.state?.players[data.seat] ?? null);

export const myHeartsHand = (data: HeartsData): Card[] => myHeartsPlayer(data)?.hand ?? [];

export const isMyHeartsTurn = (data: HeartsData) =>
  data.seat !== null && data.state?.turnSeat === data.seat;
